import type { FC, JSX, ReactNode } from "react";
import type { EditableFileOps } from "@/components/common/hooks/useEditableAttachments";
import { Button } from "@/components/ui/button";
import { CommentCard } from "@/features/study-group-stream/components/CommentCard";
import { ReplyCard } from "@/features/study-group-stream/components/ReplyCard";
import type { StreamActivity } from "@/features/study-group-stream/types/streamActivity";
import {
  LINK_BUTTON_CLASS,
  REPLY_INDENT_CLASS,
} from "@/features/study-group-stream/utils/commentSectionLayout";
import { INITIAL_REPLIES_VISIBLE } from "@/features/study-group-stream/utils/commentSectionUtils";
import type { Comment, ReactionType } from "@/lib/humhub-api/types";

type CommentWithCapabilities = Comment & {
  capabilities?: { can_edit: boolean; can_delete: boolean };
};

export type CommentWithVisibility = CommentWithCapabilities & {
  visibleReplies: CommentWithCapabilities[];
  hiddenRepliesCount: number;
  areRepliesExpanded: boolean;
};

interface CommentThreadProps {
  comment: CommentWithVisibility;
  replyCtx: { parentId: number; anchorId: number } | null;
  highlightedCommentIds: ReadonlySet<number>;
  newCommentIds: ReadonlySet<number>;
  newCommentActivities?: StreamActivity[];
  onReaction: (
    commentId: number,
    reaction: ReactionType | null,
  ) => Promise<void>;
  onReplyClick: (parentId: number, anchorId: number) => void;
  onDeleteComment: (commentId: number) => Promise<void>;
  onEditComment: (
    commentId: number,
    newMessage: string,
    fileOps?: EditableFileOps,
  ) => Promise<void>;
  onToggleReplies: (commentId: number, areRepliesExpanded: boolean) => void;
  isCurrentUserAuthor: (comment: Comment) => boolean;
  currentUserId?: number;
  topicIds?: Array<string | number>;
  onSendMessage?: (userId: number) => void;
  renderReplyInput: (
    parentId: number,
    anchorId: number,
    variant: "comment" | "reply",
  ) => JSX.Element | null;
  renderHiddenRepliesLabel: (count: number, isExpanded: boolean) => ReactNode;
  isArchived?: boolean;
  canCreateComments?: boolean;
}

export const CommentThread: FC<CommentThreadProps> = ({
  comment,
  replyCtx,
  highlightedCommentIds,
  newCommentIds,
  newCommentActivities = [],
  onReaction,
  onReplyClick,
  onDeleteComment,
  onEditComment,
  onToggleReplies,
  isCurrentUserAuthor,
  currentUserId,
  topicIds,
  onSendMessage,
  renderReplyInput,
  renderHiddenRepliesLabel,
  isArchived = false,
  canCreateComments = true,
}) => {
  const commentCapabilities = comment.capabilities;
  const visibleReplyIds = new Set(
    comment.visibleReplies.map((reply) => Number(reply.id)),
  );
  const hiddenUnreadReplies = newCommentActivities.filter(
    (activity) =>
      activity.type === "reply" &&
      activity.parentCommentId === Number(comment.id) &&
      !visibleReplyIds.has(activity.id),
  );
  const hiddenMentionCount = hiddenUnreadReplies.filter(
    (activity) => activity.isMention,
  ).length;

  return (
    <div className="mt-1 min-w-0 overflow-visible rounded-2xl bg-[#ebf0f2] px-2 py-1">
      <CommentCard
        comment={{ ...comment, comments: undefined }}
        onReaction={onReaction}
        onReplyClick={
          canCreateComments
            ? (anchorId) => onReplyClick(comment.id, anchorId)
            : undefined
        }
        onDeleteComment={onDeleteComment}
        onEditComment={onEditComment}
        isCurrentUserAuthor={isCurrentUserAuthor(comment)}
        canEdit={commentCapabilities?.can_edit}
        canDelete={commentCapabilities?.can_delete}
        currentUserId={currentUserId}
        onSendMessage={onSendMessage}
        isCommentActive={replyCtx?.anchorId === comment.id}
        isHighlighted={highlightedCommentIds.has(Number(comment.id))}
        isNew={newCommentIds.has(Number(comment.id))}
        isMention={newCommentActivities.some(
          (activity) =>
            activity.id === Number(comment.id) && activity.isMention,
        )}
        disabled={isArchived}
        topicIds={topicIds}
      />

      {!isArchived &&
        canCreateComments &&
        renderReplyInput(comment.id, comment.id, "comment")}

      {comment.visibleReplies.map((reply) => (
        <div key={reply.id} className={`mt-1 min-w-0 ${REPLY_INDENT_CLASS}`}>
          <ReplyCard
            comment={reply}
            onReaction={onReaction}
            onReplyClick={
              canCreateComments
                ? () => onReplyClick(comment.id, reply.id)
                : undefined
            }
            onDeleteComment={onDeleteComment}
            onEditComment={onEditComment}
            isCurrentUserAuthor={isCurrentUserAuthor(reply)}
            canEdit={reply.capabilities?.can_edit}
            canDelete={reply.capabilities?.can_delete}
            currentUserId={currentUserId}
            onSendMessage={onSendMessage}
            isCommentActive={replyCtx?.anchorId === reply.id}
            isHighlighted={highlightedCommentIds.has(Number(reply.id))}
            isNew={newCommentIds.has(Number(reply.id))}
            isMention={newCommentActivities.some(
              (activity) =>
                activity.id === Number(reply.id) && activity.isMention,
            )}
            disabled={isArchived}
          />
          {!isArchived &&
            canCreateComments &&
            renderReplyInput(comment.id, reply.id, "reply")}
        </div>
      ))}

      {(comment.hiddenRepliesCount > 0 ||
        (comment.areRepliesExpanded &&
          comment.visibleReplies.length > INITIAL_REPLIES_VISIBLE)) && (
        <div
          className={`mb-2 mt-1 min-w-0 ${REPLY_INDENT_CLASS}`}
          data-reply-toggle-comment-id={comment.id}
        >
          <Button
            variant="ghost"
            size="sm"
            className={LINK_BUTTON_CLASS}
            onClick={() =>
              onToggleReplies(comment.id, comment.areRepliesExpanded)
            }
          >
            {renderHiddenRepliesLabel(
              comment.hiddenRepliesCount,
              comment.areRepliesExpanded,
            )}
            {!comment.areRepliesExpanded && hiddenMentionCount > 0 ? (
              <output
                className="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-5 text-white"
                aria-label={`${hiddenMentionCount}件の未読メンション`}
              >
                {hiddenMentionCount}
              </output>
            ) : !comment.areRepliesExpanded &&
              hiddenUnreadReplies.length > 0 ? (
              <output
                className="ml-1 inline-block size-2 rounded-full bg-sky-500"
                aria-label="未読返信あり"
              />
            ) : null}
          </Button>
        </div>
      )}
    </div>
  );
};
